import * as React from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { useNavigate } from 'react-router-dom';
import { signIn } from './service';
import { useToastContext } from '../../components/toast-context';
import Layout from '../../components/layout';
import { CircularProgress, Stack } from '@mui/material';

// --- PREMIUM UI STYLES ---
const premiumInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '16px',
    backgroundColor: '#F8FAFC', // Subtle modern gray
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '& fieldset': {
      border: 'none', // Remove default harsh border
    },
    '&:hover': {
      backgroundColor: '#F1F5F9',
    },
    '&.Mui-focused': {
      backgroundColor: '#FFFFFF',
      boxShadow:
        '0 0 0 3px rgba(16, 185, 129, 0.2), 0 4px 12px rgba(0,0,0,0.06)',
    },
    '& .MuiInputBase-input': {
      fontWeight: 600,
      color: '#0F172A',
      fontSize: '1rem',
      padding: '14px 16px',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#64748B',
    fontWeight: 600,
    fontSize: '0.95rem',
    '&.Mui-focused': { color: '#10B981', fontWeight: 700 },
  },
};
// --------------------------

export default function SignIn() {
  const { showToast } = useToastContext();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = event => {
    event.preventDefault();
    setLoading(true);
    const data = new FormData(event.currentTarget);
    signIn(data.get('phonenumber'), data.get('password'))
      .then(() => navigate('/sell'))
      .catch(() => {
        showToast('نام کاربری یا رمز عبور اشتباه است', 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      dir="rtl"
      sx={{
        py: { xs: 4, sm: 8 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}
    >
      <CssBaseline />
      <Box
        sx={{
          backgroundColor: '#ffffff',
          borderRadius: '24px',
          boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(226, 232, 240, 0.8)', // Slate 200
          p: { xs: 3, sm: 5 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle decorative background glow */}
        <Box
          sx={{
            position: 'absolute',
            top: -60,
            left: -60,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, rgba(255,255,255,0) 70%)',
            pointerEvents: 'none',
          }}
        />

        {/* Premium App-style Icon */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: '0 10px 20px -5px rgba(16, 185, 129, 0.4)',
            mb: 3,
            zIndex: 1,
          }}
        >
          <LockOutlinedIcon sx={{ color: '#fff', fontSize: 32 }} />
        </Box>

        <Typography
          component="h1"
          variant="h4"
          sx={{
            fontWeight: 800,
            color: '#0F172A',
            mb: 0.5,
            zIndex: 1,
          }}
        >
          ورود
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#64748B',
            fontWeight: 500,
            mb: 4,
            zIndex: 1,
          }}
        >
          جهت ورود به پنل اطلاعات خود را وارد کنید
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{ width: '100%', zIndex: 1 }}
        >
          <Stack spacing={2.5}>
            <TextField
              required
              fullWidth
              id="phonenumber"
              label="شماره موبایل"
              name="phonenumber"
              autoComplete="phonenumber"
              autoFocus
              dir="rtl"
              InputProps={{
                sx: {
                  ...premiumInputSx['& .MuiOutlinedInput-root'],
                  direction: 'ltr',
                },
              }}
              InputLabelProps={{
                sx: premiumInputSx['& .MuiInputLabel-root'],
              }}
              sx={premiumInputSx}
            />

            <TextField
              required
              fullWidth
              name="password"
              label="رمز عبور"
              type="password"
              id="password"
              autoComplete="current-password"
              dir="rtl"
              InputProps={{
                sx: {
                  ...premiumInputSx['& .MuiOutlinedInput-root'],
                  direction: 'ltr',
                },
              }}
              InputLabelProps={{
                sx: premiumInputSx['& .MuiInputLabel-root'],
              }}
              sx={premiumInputSx}
            />
          </Stack>

          <Button
            type="submit"
            fullWidth
            disabled={loading}
            variant="contained"
            sx={{
              mt: 4,
              mb: 1,
              py: 1.8,
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              color: '#fff',
              fontSize: '1.05rem',
              fontWeight: 700,
              boxShadow: '0 8px 20px -6px rgba(16, 185, 129, 0.5)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 24px -8px rgba(16, 185, 129, 0.6)',
              },
              '&:disabled': {
                background: '#CBD5E1', // Slate 300
                color: '#FFFFFF',
                boxShadow: 'none',
              },
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={22} sx={{ color: '#fff' }} />
                <Typography sx={{ fontWeight: 600 }}>در حال ورود...</Typography>
              </Box>
            ) : (
              'ورود به حساب'
            )}
          </Button>
        </Box>
      </Box>
    </Container>
  );
}
